import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  type?: 'website' | 'article' | 'profile';
  image?: string;
  noindex?: boolean;
  jsonLd?: object;
}

const defaultMeta = {
  title: 'Hüseyin Cem Ateş | Full-Stack Developer & Digital Solutions Expert',
  description: 'Expert full-stack developer specializing in SaaS, mobile apps, and digital solutions. Building scalable applications that make a difference.',
  image: 'https://cemates.me/og-image.jpg',
  url: 'https://cemates.me',
};

export const SEO: React.FC<SEOProps> = ({
  title,
  description = defaultMeta.description,
  canonical,
  type = 'website',
  image = defaultMeta.image,
  noindex = false,
  jsonLd,
}) => {
  const pageTitle = title 
    ? `${title} | Cemates.me` 
    : defaultMeta.title;
  
  const canonicalUrl = canonical 
    ? `${defaultMeta.url}${canonical}` 
    : defaultMeta.url;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="title" content={pageTitle} />
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Canonical */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Cemates.me" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@cematesh" />
      <meta name="twitter:creator" content="@cematesh" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      
      {/* Additional Meta */}
      <meta name="author" content="Hüseyin Cem Ateş" />
      <meta name="publisher" content="Cemates.me" />
      
      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

// Pre-defined JSON-LD schemas
export const homePageJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Person",
      "@id": "https://cemates.me/#person",
      "name": "Hüseyin Cem Ateş",
      "alternateName": ["Cem Ateş", "cemates"],
      "url": "https://cemates.me",
      "image": "https://cemates.me/og-image.jpg",
      "description": "Full-stack developer specializing in modern web applications, mobile solutions, and digital products.",
      "jobTitle": "Full-Stack Developer",
      "knowsAbout": [
        "Web Development",
        "React",
        "TypeScript",
        "Node.js",
        "Mobile App Development",
        "SaaS Development",
        "E-commerce Solutions",
        "Digital Products"
      ],
      "sameAs": [
        "https://github.com/cemates",
        "https://www.linkedin.com/in/cematesh/",
        "https://twitter.com/cematesh",
        "https://instagram.com/cemates"
      ]
    },
    {
      "@type": "WebSite",
      "@id": "https://cemates.me/#website",
      "url": "https://cemates.me",
      "name": "Cemates.me",
      "description": "Full-stack developer portfolio and digital products by Hüseyin Cem Ateş",
      "publisher": {
        "@id": "https://cemates.me/#person"
      },
      "inLanguage": ["en", "tr", "pl", "hu"]
    },
    {
      "@type": "ProfessionalService",
      "@id": "https://cemates.me/#service",
      "name": "Cemates Development Services",
      "provider": {
        "@id": "https://cemates.me/#person"
      },
      "serviceType": [
        "Web Development",
        "Mobile App Development",
        "SaaS Development",
        "E-commerce Development",
        "Technical Consultation"
      ],
      "areaServed": "Worldwide",
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Development Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Free Consultation",
              "description": "15-minute discovery call to discuss your project needs"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Custom Web Development",
              "description": "Tailored web applications built with modern technologies"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "SaaS Development",
              "description": "End-to-end SaaS product development and consulting"
            }
          }
        ]
      }
    },
    {
      "@type": "ItemList",
      "@id": "https://cemates.me/#products",
      "name": "Free Tools & Extensions",
      "description": "Free browser extensions and tools for productivity",
      "itemListElement": [
        {
          "@type": "SoftwareApplication",
          "name": "Local Route",
          "applicationCategory": "BrowserApplication",
          "operatingSystem": "Chrome, Firefox, Edge",
          "description": "Browser extension for localhost routing and development"
        },
        {
          "@type": "SoftwareApplication",
          "name": "Phonetic Pro",
          "applicationCategory": "EducationalApplication",
          "description": "Interactive phonetic alphabet learning tool"
        }
      ]
    }
  ]
};

export const aboutPageJsonLd = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "mainEntity": {
    "@type": "Person",
    "name": "Hüseyin Cem Ateş",
    "jobTitle": "Full-Stack Developer",
    "description": "Experienced full-stack developer with expertise in React, TypeScript, Node.js, and modern web technologies.",
    "url": "https://cemates.me/about",
    "sameAs": [
      "https://github.com/cemates",
      "https://www.linkedin.com/in/cematesh/"
    ]
  }
};

export const contactPageJsonLd = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  "mainEntity": {
    "@type": "Person",
    "name": "Hüseyin Cem Ateş",
    "email": "contact@cemates.me",
    "url": "https://cemates.me/contact"
  }
};

export const servicesPageJsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  "provider": {
    "@type": "Person",
    "name": "Hüseyin Cem Ateş"
  },
  "serviceType": "Web Development",
  "areaServed": "Worldwide"
};

// Breadcrumb generator for any page
export const generateBreadcrumbJsonLd = (items: { name: string; url: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": `https://cemates.me${item.url}`
  }))
});

// Pre-defined breadcrumbs for common pages
export const breadcrumbs = {
  about: generateBreadcrumbJsonLd([
    { name: "Home", url: "/" },
    { name: "About", url: "/about" }
  ]),
  contact: generateBreadcrumbJsonLd([
    { name: "Home", url: "/" },
    { name: "Contact", url: "/contact" }
  ]),
  services: generateBreadcrumbJsonLd([
    { name: "Home", url: "/" },
    { name: "Services", url: "/services" }
  ]),
  portfolio: generateBreadcrumbJsonLd([
    { name: "Home", url: "/" },
    { name: "Portfolio", url: "/portfolio" }
  ]),
  consultation: generateBreadcrumbJsonLd([
    { name: "Home", url: "/" },
    { name: "Consultation", url: "/consultation" }
  ]),
  store: generateBreadcrumbJsonLd([
    { name: "Home", url: "/" },
    { name: "Store", url: "/store" }
  ]),
  privacy: generateBreadcrumbJsonLd([
    { name: "Home", url: "/" },
    { name: "Privacy Policy", url: "/privacy" }
  ]),
  terms: generateBreadcrumbJsonLd([
    { name: "Home", url: "/" },
    { name: "Terms of Service", url: "/terms" }
  ]),
  cookies: generateBreadcrumbJsonLd([
    { name: "Home", url: "/" },
    { name: "Cookie Policy", url: "/cookies" }
  ]),
  disclaimer: generateBreadcrumbJsonLd([
    { name: "Home", url: "/" },
    { name: "Disclaimer", url: "/disclaimer" }
  ]),
  sitemap: generateBreadcrumbJsonLd([
    { name: "Home", url: "/" },
    { name: "Sitemap", url: "/sitemap" }
  ]),
  projects: generateBreadcrumbJsonLd([
    { name: "Home", url: "/" },
    { name: "Portfolio", url: "/portfolio" },
    { name: "All Projects", url: "/projects" }
  ])
};

// FAQ Schema for Consultation page
export const consultationFaqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is included in the free consultation?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The free 15-minute discovery call includes discussing your project requirements, understanding your goals, exploring potential solutions, and providing initial recommendations for your web development, mobile app, or SaaS project."
      }
    },
    {
      "@type": "Question",
      "name": "What types of projects do you work on?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "I specialize in custom web applications (React, TypeScript, Node.js), mobile apps (React Native, Flutter), SaaS development, browser extensions, e-commerce solutions, and technical consulting for startups and businesses."
      }
    },
    {
      "@type": "Question",
      "name": "How long does a typical project take?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Project timelines vary based on complexity. Simple websites take 2-4 weeks, web applications 4-12 weeks, and complex SaaS products 3-6 months. During the consultation, I provide accurate estimates based on your specific requirements."
      }
    },
    {
      "@type": "Question",
      "name": "Do you offer ongoing support after project completion?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, I offer maintenance packages and ongoing support for all projects. This includes bug fixes, feature updates, performance optimization, and technical support to ensure your application runs smoothly."
      }
    },
    {
      "@type": "Question",
      "name": "What is your pricing model?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "I offer flexible pricing options including fixed-price projects, hourly consulting rates, and retainer packages for ongoing work. Pricing is discussed during the free consultation based on your project scope and requirements."
      }
    }
  ]
};
